import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FamilyAvatar } from '../../components/FamilyAvatar';
import ComposeMessageModal from '../../components/ComposeMessageModal';
import { formatDateTime } from '../../utils/datetime';
import { useAppStore } from '../../App';

interface AdminUserRow {
 mo_peer_hash: string;
 email: string;
 name: string | null;
 profile_image: string | null;
 is_admin: boolean;
 is_active: boolean;
 created_at: string;
 last_login_at: string | null;
 family_count: number;
 barter_family_count: number;
 campaign_count: number;
}

interface MessageTarget {
 hash: string;
 name: string;
 email?: string;
}

// 상대 시간 — "3일 전" 식. 1년 넘으면 "오래전".
function relativeTime(iso: string): string {
 const diffMs = Date.now() - new Date(iso).getTime();
 const min = Math.floor(diffMs / 60000);
 const hr = Math.floor(min / 60);
 const day = Math.floor(hr / 24);
 const month = Math.floor(day / 30);
 const year = Math.floor(day / 365);
 if (year >= 1) return '오래전';
 if (month >= 1) return `${month}개월 전`;
 if (day >= 1) return `${day}일 전`;
 if (hr >= 1) return `${hr}시간 전`;
 if (min >= 1) return `${min}분 전`;
 return '방금';
}

// 요약 텍스트 표시 + 마우스 롤오버(title)로 정확 시각. 모바일은 탭하면 정확 시각 토글.
const TimeCell: React.FC<{ iso: string | null; display: string }> = ({ iso, display }) => {
 const [showExact, setShowExact] = useState(false);
 if (!iso) return <span className="text-gray-400">—</span>;
 const exact = formatDateTime(iso);
 return (
  <span
   title={exact}
   onClick={() => setShowExact(v => !v)}
   className="cursor-pointer border-b border-dotted border-gray-300"
  >
   {showExact ? exact : display}
  </span>
 );
};

const UsersAdmin: React.FC = () => {
 const navigate = useNavigate();
 const [rows, setRows] = useState<AdminUserRow[]>([]);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [sudoTarget, setSudoTarget] = useState<string | null>(null);
 const [messageTarget, setMessageTarget] = useState<MessageTarget | null>(null);
 // 스텝 연결(로그인 마스커레이드, 114) — 이 계정으로 로그인 허용할 스텝 이메일 관리.
 const [aliasTarget, setAliasTarget] = useState<{ hash: string; name: string } | null>(null);
 const [aliasList, setAliasList] = useState<Array<{ id: number; aliasEmail: string; pinned: boolean }>>([]);
 const [aliasEmail, setAliasEmail] = useState('');
 const [aliasBusy, setAliasBusy] = useState(false);
 const [aliasErr, setAliasErr] = useState<string | null>(null);
 const { user } = useAppStore();

 const loadAliases = async (hash: string) => {
  try {
   const r = await fetch(`/admin/login-alias?target=${encodeURIComponent(hash)}`, { credentials: 'include' });
   const j = await r.json();
   if (j.success) setAliasList(j.data ?? []);
  } catch { /* 무시 */ }
 };
 const openAlias = (hash: string, name: string) => {
  setAliasTarget({ hash, name }); setAliasEmail(''); setAliasErr(null); setAliasList([]);
  void loadAliases(hash);
 };
 const addAlias = async () => {
  if (!aliasTarget || !aliasEmail.trim()) return;
  setAliasBusy(true); setAliasErr(null);
  try {
   const r = await fetch('/admin/login-alias', {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ alias_email: aliasEmail.trim(), target_mo_peer_hash: aliasTarget.hash }),
   });
   const j = await r.json();
   if (j.success) { setAliasEmail(''); await loadAliases(aliasTarget.hash); }
   else setAliasErr(j.error || '등록 실패');
  } catch { setAliasErr('등록 실패'); } finally { setAliasBusy(false); }
 };
 const removeAlias = async (id: number) => {
  if (!aliasTarget) return;
  try {
   const r = await fetch(`/admin/login-alias/${id}`, { method: 'DELETE', credentials: 'include' });
   if ((await r.json()).success) await loadAliases(aliasTarget.hash);
  } catch { /* 무시 */ }
 };

 // 고객 계정 사전생성 + 클레임 코드
 const [precreateName, setPrecreateName] = useState('');
 const [precreating, setPrecreating] = useState(false);
 const [unclaimed, setUnclaimed] = useState<Array<{ moPeerHash: string; name: string | null; claimCode: string; createdAt: string }>>([]);

 const loadUnclaimed = () => {
  fetch('/admin/precreated', { credentials: 'include' })
   .then(r => r.json())
   .then(j => { if (j.success) setUnclaimed(j.data ?? []); })
   .catch(() => {});
 };

 const handlePrecreate = async () => {
  const name = precreateName.trim();
  if (!name) return;
  setPrecreating(true);
  try {
   const r = await fetch('/admin/precreate-account', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
   });
   const j = await r.json();
   if (!j.success) { alert(j.error || '사전생성 실패'); return; }
   alert(`계정 사전생성 완료.\n\n고객에게 전달할 인계 코드:\n\n${j.data.claimCode}\n\n고객이 로그인 화면 "인계 코드가 있으신가요?"에서 입력 + Google 로그인하면 이 계정을 인수합니다.`);
   setPrecreateName('');
   loadUnclaimed();
  } catch (err: any) {
   alert(err?.message || '사전생성 실패');
  } finally {
   setPrecreating(false);
  }
 };

 // 강제 인계 — 고객이 코드 전에 일반가입한 경우: 그 가입 계정을 버리고 신원을 이 사전생성 계정에 붙임.
 const handleForceClaim = async (preCreatedHash: string, name: string | null) => {
  const email = prompt(`"${name || '(이름 없음)'}" 계정을 넘겨줄 고객의 로그인 이메일을 입력하세요.\n\n(고객이 코드 없이 이미 가입한 그 Google 이메일. 그 계정은 폐기되고, 이 사전생성 계정에 붙습니다.)`);
  if (!email?.trim()) return;
  if (!confirm(`정말 인계할까요?\n\n- ${email.trim()} 로 가입한 계정은 폐기(재로그인 불가)\n- 이 사전생성 계정(${name || preCreatedHash.slice(0, 14)})이 그 이메일로 로그인되게 설정\n- 고객은 Google 재로그인만 하면 됩니다`)) return;
  try {
   const r = await fetch('/admin/force-claim', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ preCreatedHash, customerEmail: email.trim() }),
   });
   const j = await r.json();
   if (!j.success) { alert(j.error || '강제 인계 실패'); return; }
   alert(`인계 완료. 이제 ${j.data.email} 로 Google 로그인하면 이 계정으로 들어갑니다.`);
   loadUnclaimed();
  } catch (err: any) {
   alert(err?.message || '강제 인계 실패');
  }
 };

 const startSudo = async (targetHash: string, targetName: string, mode: 'view' | 'operate' = 'view') => {
  // 열쇠(🔑 운영 위임, operate)는 확인 팝업 없이 바로 진입 — 관리자 반복 작업 마찰 제거.
  // view-as(행 클릭)는 기존대로 확인 유지. 양쪽 모두 서버에서 audit log 기록 + 대상 알림.
  if (mode === 'view') {
   const msg = `${targetName} 사용자로 view-as 진입하시겠습니까?\n\n진입 시:\n- 읽기 전용 (모든 write 차단)\n- 메시지함/채팅 열람 불가\n- 대상에게 알림 발송\n- audit log 기록`;
   if (!confirm(msg)) return;
  }
  setSudoTarget(targetHash);
  try {
   const r = await fetch('/admin/sudo/start', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mo_peer_hash: targetHash, mode }),
   });
   const j = await r.json();
   if (!j.success) {
    alert(j.error || 'view-as 시작 실패');
    setSudoTarget(null);
    return;
   }
   // 위임 진입 후엔 대상 사용자(비관리자) 컨텍스트가 된다 → 관리자 페이지에 남아 있으면
   // /admin/* 이 403("관리자 권한 필요")을 낸다. 대상의 대시보드로 이동해 그 사용자로 구성.
   window.location.href = '/dashboard';
  } catch (err: any) {
   alert(err?.message || 'view-as 시작 실패');
   setSudoTarget(null);
  }
 };

 useEffect(() => {
  setLoading(true);
  fetch('/admin/users', { credentials: 'include' })
   .then(r => {
    if (r.status === 403) throw new Error('관리자 권한 필요');
    return r.json();
   })
   .then(j => {
    if (!j.success) throw new Error(j.error || '조회 실패');
    setRows((j.data ?? []).map((r: any) => ({
     ...r,
     family_count: Number(r.family_count),
     barter_family_count: Number(r.barter_family_count),
     campaign_count: Number(r.campaign_count),
    })));
   })
   .catch(err => setError(err.message))
   .finally(() => setLoading(false));
  loadUnclaimed();
 }, []);

 if (error) return <div className="p-6 bg-red-50 text-red-700">{error}</div>;

 const totalFamilies = rows.reduce((sum, r) => sum + r.family_count, 0);
 const totalCampaigns = rows.reduce((sum, r) => sum + r.campaign_count, 0);

 return (
  <div className="p-4 md:p-6">
   <h1 className="text-2xl font-bold mb-4">회원</h1>
   <div className="grid grid-cols-3 gap-3 mb-6 max-w-md">
    <div className="bg-blue-50 border border-blue-200 p-3">
     <div className="text-xs text-gray-600">회원 수</div>
     <div className="text-2xl font-bold text-blue-700">{rows.length}</div>
    </div>
    <div className="bg-emerald-50 border border-emerald-200 p-3">
     <div className="text-xs text-gray-600">모패밀리 (활성)</div>
     <div className="text-2xl font-bold text-emerald-700">{totalFamilies}</div>
    </div>
    <div className="bg-purple-50 border border-purple-200 p-3">
     <div className="text-xs text-gray-600">캠페인 (활성)</div>
     <div className="text-2xl font-bold text-purple-700">{totalCampaigns}</div>
    </div>
   </div>

   {/* 고객 계정 사전생성 → 클레임 코드 인계 */}
   <div className="border border-gray-200 bg-gray-50 p-4 mb-6 max-w-2xl">
    <div className="font-semibold text-gray-800 mb-1">고객 계정 미리 만들기</div>
    <p className="text-xs text-gray-500 mb-3">
     고객 대신 계정을 만들어 구성한 뒤(아래 목록에서 🔑 운영 위임으로 설정), 발급된 인계 코드를 고객에게 전달합니다.
     고객이 로그인 화면 "인계 코드가 있으신가요?"에서 코드 입력 + Google 로그인 시 이 계정을 인수합니다.
    </p>
    <div className="flex gap-2 mb-3">
     <input
      value={precreateName}
      onChange={(e) => setPrecreateName(e.target.value)}
      placeholder="고객/업체 이름"
      className="flex-1 border border-gray-300 px-3 py-2 text-sm"
     />
     <button
      type="button"
      onClick={handlePrecreate}
      disabled={precreating || !precreateName.trim()}
      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm"
     >
      {precreating ? '생성 중...' : '사전생성'}
     </button>
    </div>
    {unclaimed.length > 0 && (
     <div>
      <div className="text-xs text-gray-500 mb-1">인계 대기 계정 ({unclaimed.length}) — 코드 전달 후 고객 로그인 시 인수됨</div>
      <div className="space-y-1">
       {unclaimed.map((u) => (
        <div key={u.moPeerHash} className="flex items-center justify-between gap-2 bg-white border border-gray-200 px-3 py-1.5 text-sm">
         <span className="text-gray-700 flex-1 truncate">{u.name || '(이름 없음)'}</span>
         <span className="font-mono text-blue-700 tracking-wider">{u.claimCode}</span>
         <button
          type="button"
          onClick={() => handleForceClaim(u.moPeerHash, u.name)}
          title="고객이 코드 없이 이미 가입했을 때: 그 가입 계정을 폐기하고 이 계정에 신원을 붙입니다."
          className="px-2 py-0.5 text-xs border border-amber-400 text-amber-700 hover:bg-amber-50 whitespace-nowrap"
         >
          가입 계정에 인계
         </button>
        </div>
       ))}
      </div>
     </div>
    )}
   </div>

   {loading ? (
    <p className="text-gray-500">로딩 중...</p>
   ) : (
    <div className="overflow-x-auto">
     <table className="w-full text-sm border border-gray-200">
      <thead className="bg-gray-50 text-gray-600">
       <tr className="text-left">
        <th className="px-3 py-2">회원</th>
        <th className="px-3 py-2">이메일</th>
        <th className="px-3 py-2 text-right">모패밀리</th>
        <th className="px-3 py-2 text-right">바터</th>
        <th className="px-3 py-2 text-right">캠페인</th>
        <th className="px-3 py-2">가입</th>
        <th className="px-3 py-2">최근 접속</th>
        <th className="px-3 py-2">상태</th>
        <th className="px-3 py-2">메시지</th>
       </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
       {rows.map(r => {
        const isSelf = r.mo_peer_hash === user?.moPeerHash;
        const canSudo = !isSelf && r.is_active;
        const isLoading = sudoTarget === r.mo_peer_hash;
        const handleClick = canSudo && !isLoading ? () => startSudo(r.mo_peer_hash, r.name || r.email) : undefined;
        return (
         <tr key={r.mo_peer_hash} className={r.is_active ? '' : 'opacity-50'}>
          <td className="px-3 py-2">
           <div className="flex items-center gap-2">
            {!isSelf && r.is_active && (
             <button
              type="button"
              onClick={() => startSudo(r.mo_peer_hash, r.name || r.email, 'operate')}
              disabled={isLoading}
              className="text-base hover:bg-amber-50 px-1 py-0.5 leading-none disabled:opacity-50 shrink-0"
              title="운영 위임 — 이 회원의 운영을 대신 (쓰기 가능 — 인큐베이팅). 계정삭제·메시지 제외, 전 작업 기록"
              aria-label="운영 위임"
             >
              🔑
             </button>
            )}
            <FamilyAvatar
             name={r.name || r.email}
             familyHash={r.mo_peer_hash}
             imageUrl={r.profile_image}
             size={28}
            />
            <button
             type="button"
             onClick={handleClick}
             disabled={!canSudo || isLoading}
             title={canSudo ? '클릭 → 이 회원으로 read-only view-as 진입' : isSelf ? '자기 자신은 view-as 불가' : '비활성 회원'}
             className={`font-medium text-left ${canSudo ? 'text-blue-700 hover:underline cursor-pointer' : 'text-gray-700 cursor-default'} disabled:opacity-60`}
            >
             {isLoading ? '진입 중...' : (r.name || '(이름 없음)')}
            </button>
            {r.is_admin && (
             <span title="관리자" aria-label="관리자" className="text-sm leading-none">⚙️</span>
            )}
           </div>
          </td>
          <td className="px-3 py-2 text-gray-600 text-xs">{r.email}</td>
          <td className="px-3 py-2 text-right font-medium">{r.family_count}</td>
          <td className="px-3 py-2 text-right text-purple-700">{r.barter_family_count}</td>
          <td className="px-3 py-2 text-right font-medium">{r.campaign_count}</td>
          <td className="px-3 py-2 text-xs text-gray-500"><TimeCell iso={r.created_at} display={relativeTime(r.created_at)} /></td>
          <td className="px-3 py-2 text-xs text-gray-500"><TimeCell iso={r.last_login_at} display={r.last_login_at ? relativeTime(r.last_login_at) : '—'} /></td>
          <td className="px-3 py-2 text-xs">
           <span
            title={r.is_active ? '활성' : '비활성'}
            aria-label={r.is_active ? '활성' : '비활성'}
            className={`inline-block w-2.5 h-2.5 rounded-full ${r.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
           />
          </td>
          <td className="px-3 py-2 text-xs">
           {!isSelf && r.is_active && (
            <div className="flex items-center gap-1">
             <button
              type="button"
              onClick={() => setMessageTarget({ hash: r.mo_peer_hash, name: r.name || r.email, email: r.email })}
              className="text-base hover:bg-blue-50 px-2 py-0.5 leading-none"
              title="이 회원에게 메시지 보내기"
              aria-label="메시지 보내기"
             >
              ✉️
             </button>
             {!r.is_admin && (
              <button
               type="button"
               onClick={() => openAlias(r.mo_peer_hash, r.name || r.email)}
               className="text-base hover:bg-green-50 px-2 py-0.5 leading-none"
               title="스텝 연결 — 이 계정으로 로그인할 스텝 구글 이메일 등록"
               aria-label="스텝 연결"
              >
               🔗
              </button>
             )}
            </div>
           )}
          </td>
         </tr>
        );
       })}
      </tbody>
     </table>
    </div>
   )}

   {messageTarget && (
    <ComposeMessageModal
     presetRecipient={messageTarget}
     onClose={() => setMessageTarget(null)}
     onSent={(cid) => {
      setMessageTarget(null);
      navigate(`/inbox/${cid}`);
     }}
    />
   )}

   {aliasTarget && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setAliasTarget(null)}>
     <div className="bg-white p-5 max-w-md w-full space-y-3" onClick={(e) => e.stopPropagation()}>
      <div>
       <h3 className="font-bold">스텝 연결 — {aliasTarget.name}</h3>
       <p className="text-sm text-gray-500">등록한 이메일로 구글 로그인하면 이 계정으로 바로 들어옵니다.</p>
      </div>
      <div className="flex gap-2">
       <input
        type="email"
        className="flex-1 border border-gray-300 px-2 py-1.5 text-sm"
        placeholder="스텝 구글 이메일"
        value={aliasEmail}
        onChange={(e) => setAliasEmail(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') void addAlias(); }}
       />
       <button className="px-3 py-1.5 text-sm bg-blue-600 text-white disabled:opacity-50" disabled={aliasBusy || !aliasEmail.trim()} onClick={() => void addAlias()}>추가</button>
      </div>
      {aliasErr && <p className="text-xs text-red-600">{aliasErr}</p>}
      <ul className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
       {aliasList.length === 0 ? (
        <li className="text-sm text-gray-400 py-2">연결된 스텝이 없습니다.</li>
       ) : aliasList.map(a => (
        <li key={a.id} className="flex items-center justify-between py-2 text-sm">
         <span>{a.aliasEmail}
          <span className={`ml-2 text-[10px] px-1 py-px ${a.pinned ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
           {a.pinned ? '로그인함' : '대기'}
          </span>
         </span>
         <button className="text-xs text-red-500 hover:underline" onClick={() => void removeAlias(a.id)}>해제</button>
        </li>
       ))}
      </ul>
      <p className="text-[11px] text-gray-400">해제는 다음 로그인부터 적용됩니다. 한 이메일은 한 계정에만 연결됩니다.</p>
      <div className="flex justify-end">
       <button className="px-3 py-1.5 text-sm border border-gray-300" onClick={() => setAliasTarget(null)}>닫기</button>
      </div>
     </div>
    </div>
   )}
  </div>
 );
};

export default UsersAdmin;
